/**
 * DataRefreshNotifications Component
 * ===================================
 * Listens for real-time data refresh notifications and shows toasts.
 * Provides subtle feedback when benchmark data is updated.
 */

import { useEffect, useRef } from 'react';
import { toast } from '@/utils/safeToast';
import { Database, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { devLog } from '@/lib/devLog';
import { API_URL } from '@/config/apiConfig';

// Custom toast styles for data notifications
const DataToast = ({ source, status, message }) => {
  const getIcon = () => {
    switch (status) {
      case 'success':
      case 'complete':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'started':
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      default:
        return <Database className="w-4 h-4 text-slate-500" />;
    }
  };

  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{getIcon()}</div>
      <div>
        <p className="text-sm font-medium text-slate-900">Data Update: {source}</p>
        <p className="text-xs text-slate-500 mt-0.5">{message}</p>
      </div>
    </div>
  );
};

const useDataRefreshNotifications = (userId, enabled = true) => {
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  useEffect(() => {
    if (!enabled || !userId) return;

    const connect = () => {
      // Close existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      try {
        const eventSource = new EventSource(
          `${API_URL}/api/notifications/stream?user_id=${userId}`,
          { withCredentials: true }
        );

        eventSource.onopen = () => {
          reconnectAttempts.current = 0;
          devLog('Connected to notification stream');
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Handle different notification types
            if (data.type === 'heartbeat' || data.type === 'connected') {
              return; // Ignore heartbeats
            }
            
            // Check if it's a data refresh notification
            if (data.data?.refresh_type === 'data_refresh' || data.type === 'data_refresh') {
              const source = data.data?.source || data.source || 'System';
              const status = data.data?.status || data.status || 'info';
              const message = data.message || 'Data has been updated';
              
              // Show toast notification
              toast.custom(
                () => <DataToast source={source} status={status} message={message} />,
                {
                  duration: status === 'started' ? 2000 : 4000,
                  position: 'bottom-right',
                }
              );
            }
          } catch (e) {
            console.error('Error parsing notification:', e);
          }
        };

        eventSource.onerror = () => {
          eventSource.close();
          
          // Attempt reconnection with exponential backoff
          if (reconnectAttempts.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
            reconnectAttempts.current++;
            
            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, delay);
          }
        };

        eventSourceRef.current = eventSource;
      } catch (e) {
        console.error('Error creating EventSource:', e);
      }
    };

    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [userId, enabled]);

  // Note: isConnected is tracked via ref, not suitable for reactive rendering
  // Use state if you need to display connection status in UI
  return {};
};

/**
 * DataRefreshNotifications Component
 * Place this in your app layout to enable real-time data refresh notifications.
 */
const DataRefreshNotifications = ({ userId, enabled = true }) => {
  useDataRefreshNotifications(userId, enabled);
  return null; // This component doesn't render anything
};

export { DataRefreshNotifications, useDataRefreshNotifications };
export default DataRefreshNotifications;
